import { useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSession } from "@/auth/session";
import {
  getContractAuditTrail,
  listContracts,
  type AuditTrail,
  type AuditTrailEvent,
  type ContractListItem,
} from "@/lib/api";

const KIND_STYLE: Record<
  AuditTrailEvent["kind"],
  { icon: string; color: string; bg: string; label: string }
> = {
  movimiento: { icon: "ri-book-2-line", color: "text-slate-600", bg: "bg-slate-100", label: "Libro APR" },
  pago: { icon: "ri-money-dollar-circle-line", color: "text-emerald-700", bg: "bg-emerald-50", label: "Pago" },
  anulacion_pago: { icon: "ri-close-circle-line", color: "text-rose-700", bg: "bg-rose-50", label: "Anulación de pago" },
  anulacion_documento: { icon: "ri-file-damage-line", color: "text-rose-700", bg: "bg-rose-50", label: "Anulación de documento" },
};

function formatCLP(value: number): string {
  return `$${Math.round(value).toLocaleString("es-CL")}`;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString("es-CL", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function toInputDate(iso: string): string {
  return iso.slice(0, 10);
}

export default function AuditoriaPage() {
  const { session } = useSession();
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ContractListItem[]>([]);
  const [selectedContract, setSelectedContract] = useState<ContractListItem | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trail, setTrail] = useState<AuditTrail | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function handleSearch(): Promise<void> {
    if (!session) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError("");
    try {
      const rows = await listContracts(session, { q, limit: 20 });
      setResults(rows);
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : String(searchError));
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function handleSelectContract(contract: ContractListItem): void {
    setSelectedContract(contract);
    setResults([]);
    setQuery(`${contract.contractNumber} — ${contract.customerName ?? ""}`);
    void loadTrail(contract.id, from, to);
  }

  async function loadTrail(contractId: string, fromValue: string, toValue: string): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const data = await getContractAuditTrail(session, contractId, {
        from: fromValue || undefined,
        to: toValue ? `${toValue}T23:59:59` : undefined,
      });
      setTrail(data);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
      setTrail(null);
    } finally {
      setLoading(false);
    }
  }

  function handleApplyDateFilter(): void {
    if (selectedContract) void loadTrail(selectedContract.id, from, to);
  }

  function handleClearDateFilter(): void {
    setFrom("");
    setTo("");
    if (selectedContract) void loadTrail(selectedContract.id, "", "");
  }

  const chartData = useMemo(() => {
    if (!trail) return [];
    return trail.eventos
      .filter((evento) => !evento.isMetadataFix)
      .map((evento, index) => ({
        index,
        fecha: toInputDate(evento.timestamp),
        etiqueta: formatDateTime(evento.timestamp),
        saldo: evento.runningBalance.total,
        label: evento.label,
      }));
  }, [trail]);

  async function handleExportExcel(): Promise<void> {
    if (!trail) return;
    const XLSX = await import("xlsx");
    const filas = trail.eventos.map((evento) => ({
      fecha: formatDateTime(evento.timestamp),
      tipo: KIND_STYLE[evento.kind].label,
      descripcion: evento.label,
      folio: evento.folio ?? "",
      monto: evento.amount,
      saldoDocumento: evento.runningBalance.document,
      saldoConvenio: evento.runningBalance.agreement,
      saldoTotal: evento.runningBalance.total,
      detalle: evento.detail ?? "",
      responsable: evento.actor ?? "",
    }));
    const hoja = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Auditoría");
    XLSX.writeFile(libro, `auditoria_${trail.contract.contractNumber}.xlsx`);
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-gray-800">Auditoría del Libro APR</h1>
        <p className="text-sm text-gray-500">
          Línea de tiempo por contrato: cargos, pagos, anulaciones y las correcciones del libro, en el orden exacto en que ocurrieron.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto_auto] gap-3 items-end">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">Contrato o cliente</label>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedContract(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && void handleSearch()}
              placeholder="Número de contrato o nombre"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
            {results.length > 0 ? (
              <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {results.map((contract) => (
                  <button
                    key={contract.id}
                    type="button"
                    onClick={() => handleSelectContract(contract)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-800">{contract.contractNumber} — {contract.customerName ?? "-"}</div>
                    <div className="text-xs text-gray-500">{contract.startAddress}</div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <button
            type="button"
            onClick={() => (query && results.length === 0 && !selectedContract ? void handleSearch() : handleApplyDateFilter())}
            disabled={searching || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {searching ? "Buscando..." : loading ? "Cargando..." : "Buscar"}
          </button>
          <button
            type="button"
            onClick={handleClearDateFilter}
            disabled={!selectedContract || loading}
            className="px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Limpiar fechas
          </button>
        </div>
        {error ? <div className="p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg">{error}</div> : null}
      </div>

      {trail ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-gray-200 p-4 md:col-span-1">
              <div className="text-xs text-gray-500">Contrato</div>
              <div className="text-base font-bold text-gray-800">{trail.contract.contractNumber}</div>
              <div className="text-sm text-gray-600 truncate">{trail.contract.customerName}</div>
              <div className="text-xs text-gray-400">{trail.contract.customerRut}</div>
            </div>
            <StatTile label="Saldo documentos" value={trail.currentBalance?.document ?? 0} />
            <StatTile label="Saldo convenios" value={trail.currentBalance?.agreement ?? 0} />
            <StatTile label="Saldo total" value={trail.currentBalance?.total ?? 0} emphasis />
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-700">Evolución del saldo</div>
              <button
                type="button"
                onClick={() => void handleExportExcel()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <i className="ri-download-2-line" /> Exportar
              </button>
            </div>
            {chartData.length > 0 ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="saldoFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#eef2f7" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={64} tickFormatter={(v: number) => formatCLP(v)} />
                    <Tooltip
                      formatter={(value: number) => [formatCLP(value), "Saldo total"]}
                      labelFormatter={(_label, payload) => (payload?.[0]?.payload as { etiqueta?: string } | undefined)?.etiqueta ?? ""}
                      contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                    />
                    <Area type="stepAfter" dataKey="saldo" stroke="none" fill="url(#saldoFill)" isAnimationActive={false} />
                    <Line type="stepAfter" dataKey="saldo" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-gray-400">Sin movimientos en el rango seleccionado.</div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">
              Línea de tiempo ({trail.eventos.length} eventos)
            </div>
            <div className="divide-y divide-gray-100">
              {trail.eventos.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">Sin eventos en el rango seleccionado.</div>
              ) : (
                trail.eventos.map((evento, index) => {
                  const estilo = KIND_STYLE[evento.kind];
                  const id = `${index}-${evento.timestamp}`;
                  const expanded = expandedId === id;
                  return (
                    <div key={id} className={evento.isMetadataFix ? "opacity-60" : ""}>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : id)}
                        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
                      >
                        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${estilo.bg} ${estilo.color}`}>
                          <i className={`${estilo.icon} text-sm`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-800">{evento.label}</span>
                            {evento.folio ? <span className="text-xs text-gray-400">folio {evento.folio}</span> : null}
                            {evento.isMetadataFix ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">técnico, no afecta saldo</span>
                            ) : null}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(evento.timestamp)}
                            {evento.actor ? ` · ${evento.actor}` : ""}
                            {evento.detail ? ` · ${evento.detail}` : ""}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`text-sm font-semibold ${evento.amount < 0 ? "text-emerald-600" : "text-gray-800"}`}>
                            {evento.amount >= 0 ? "+" : ""}{formatCLP(evento.amount)}
                          </div>
                          <div className="text-xs text-gray-400">saldo {formatCLP(evento.runningBalance.total)}</div>
                        </div>
                        <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-gray-400 flex-shrink-0 mt-1`} />
                      </button>
                      {expanded ? (
                        <pre className="mx-4 mb-3 p-3 text-xs bg-gray-50 border border-gray-100 rounded-lg overflow-auto text-gray-600">
                          {JSON.stringify(evento.raw, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : !loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-sm text-gray-400">
          Busca un contrato para ver su línea de tiempo en el Libro APR.
        </div>
      ) : null}
    </div>
  );
}

function StatTile({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return (
    <div className={`bg-white rounded-2xl border p-4 ${emphasis ? "border-blue-200 bg-blue-50/40" : "border-gray-200"}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-bold ${emphasis ? "text-blue-700" : "text-gray-800"}`}>{formatCLP(value)}</div>
    </div>
  );
}
